/**
 * READING ORDER FOR ONE CALL — the pure half of the transcript.
 *
 * SPLIT OUT OF `transcript-thread.tsx` SO IT CAN BE TESTED WITHOUT A BROWSER.
 * Everything here is a function from rows to rows: no React, no DOM, no imports
 * that drag either in. `tests/snviewercheck.mjs` bundles this file alone and
 * asserts the orderings below against a real stored payload, which is how the
 * defect that prompted the split was found — see `unclocked`.
 */
export interface TranscriptTurn {
  n: number
  userText: string | null
  assistantText: string | null
  userSpeechStoppedAt: number | null
  clientSpeechEndAt: number | null
  responseCreatedAt: number | null
  audioBufferStartedAt: number | null
  audioBufferStoppedAt: number | null
  responseDoneAt: number | null
}

export interface TranscriptToolCall {
  callId: string
  turnN: number | null
  name: string
  args: unknown
  result: unknown
  ok: boolean | null
  errorMessage: string | null
  /** Epoch ms — the session's clock, so it interleaves with the turn anchors. */
  startedAt: number
  endedAt: number | null
  durationMs: number | null
  orphaned: boolean
}

/** A capture-gap marker. The viewer passes none — its payload has no event log —
 *  which is a documented difference from the panel, not an oversight. */
export interface TranscriptMarker {
  id: string
  atMs: number
  kind: string
  detail: unknown
}
/**
 * The kinds of `session_events` row that belong IN the transcript rather than in
 * a log nobody opens.
 *
 * A sideband detach is not metadata about the transcript — it is a hole in it,
 * and it has to appear in the reading order at the point where the words stop.
 * The match is deliberately loose (any kind mentioning a gap, a sideband or a
 * re-attach) because the Durable Object owns this vocabulary and will grow it;
 * a hard-coded list would silently drop the next kind somebody adds, which is
 * the failure mode that matters here — a missing marker reads as continuous
 * speech.
 */
/**
 * Did our capture ACTUALLY stop here?
 *
 * This matched any kind containing 'sideband', which meant `sideband.rate_limits`
 * — a routine informational event the provider emits several times a minute — was
 * rendered as "our capture was interrupted here — anything said during this
 * interval is not in the transcript below". So did `sideband.attached`, which is
 * the moment capture BEGINS. A normal call was littered with false interruption
 * markers, which is the same failure as the OPFS banner: cry wolf often enough and
 * the one real interruption is furniture.
 *
 * An allowlist, not a substring match, so a new `sideband.*` event cannot silently
 * start claiming the transcript is incomplete.
 */
const CAPTURE_INTERRUPTED: readonly string[] = [
  'sideband.detached',
  'sideband.closed',
  'sideband.reattached',
  'sideband.attach_failed',
  'sideband.unrecoverable',
  'control.closed',
  'media.closed',
]

/**
 * THE ALLOWLIST STILL CRIED WOLF ON EVERY CLEAN CALL, because the three sockets
 * closing IS how a normal hangup looks.
 *
 * Measured on all seven real sessions, every one of them `ENDED_NORMAL` with
 * `client_intent` and `sideband_reattaches = 0`: each renders exactly three
 * "our capture was interrupted here — anything said during this interval is not in
 * the transcript below" boxes at the foot of a complete transcript. The ordering is
 * identical every time, relative to the DO's own `session.ended`:
 *
 *   media.closed    −3 to −16 ms   (the browser closes its uplink first; that is
 *                                   what triggers the end)
 *   control.closed    0 to +7 ms
 *   sideband.closed  +2.09 to +2.15 s  (code 1006, no CLOSE frame — §1)
 *
 * A socket closing as part of teardown is not a hole in the transcript, and three
 * false alarms per call is how the one real interruption becomes furniture. So the
 * marker is suppressed from the moment the session ended, with a one-second window
 * on the leading edge for `media.closed` — bounded by the observed −16 ms and by
 * the fact that it is a single browser→DO round trip, so a genuine mid-call drop a
 * second or more before the end still gets its marker.
 *
 * `gap` and `reattach` kinds are NEVER suppressed: those are claims about lost
 * content, not about a socket, and they are meaningful whenever they occur.
 */
const TEARDOWN_GRACE_MS = 1_000

export function isGapEvent(kind: string, atMs: number, endedAtMs: number | null): boolean {
  const k = kind.toLowerCase()
  if (k.includes('gap')) return true
  if (k.includes('reattach')) return true
  if (!CAPTURE_INTERRUPTED.includes(k)) return false
  return endedAtMs === null || atMs < endedAtMs - TEARDOWN_GRACE_MS
}

/**
 * When this session ended, on the SAME CLOCK as `session_events.at_ms`.
 *
 * `sessions.ended_at` is an ISO string from the row and would do, but the DO logs
 * `session.ended` with its own epoch-ms arrival time and every marker is compared
 * against that — mixing the two would reintroduce a millisecond skew into the one
 * comparison that decides whether a warning is shown. Falls back to the row.
 */
export function sessionEndMs(events: TranscriptMarker[], endedAtIso: string | null): number | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]
    if (e && e.kind === 'session.ended') return e.atMs
  }
  if (!endedAtIso) return null
  const t = Date.parse(endedAtIso)
  return Number.isNaN(t) ? null : t
}

export type Entry =
  | {
      kind: 'said'
      /**
       * Epoch ms, or NULL when this row has no clock of its own.
       *
       * Null is only ever produced by the no-anchor path below, and it means
       * exactly one thing: we do not know when this was said. It is rendered as
       * blank rather than as a time, because the alternative — which this
       * component did until a real payload was put through it — was to render
       * the synthetic ordering index as if it were a timestamp, and print
       * 19:00:00 (epoch zero, localised) against every line of a call that
       * happened this afternoon.
       */
      at: number | null
      /** When this entry STOPPED, where an anchor for that exists: the playout
       *  stop for an assistant line, the speech stop for a user line (whose `at`
       *  is already its end). Null when never observed. Playback highlighting
       *  uses it; the sort does not. */
      until: number | null
      who: 'user' | 'assistant'
      n: number
      text: string
    }
  | { kind: 'tool'; at: number | null; until: number | null; call: TranscriptToolCall }
  | { kind: 'marker'; at: number; event: TranscriptMarker }

/**
 * Interleave turns, tool calls and gap events onto one timeline.
 *
 * EVERY ENTRY GETS A REAL NUMBER, in a forward pass, so the sort is total. A
 * turn whose anchors are all null — which happens exactly when the sideband was
 * detached for that turn — inherits the previous entry's time plus a
 * millisecond. That keeps it in conversational order instead of sorting it to
 * one end, and the marker beside it explains why it has no clock of its own.
 *
 * Tool calls carry their own `startedAt` on the Durable Object's clock, so they
 * slot into the reading order where the model actually reached for the tool —
 * between the user's question and the answer that used the result.
 */
export function buildTranscript(
  turns: TranscriptTurn[],
  toolCalls: TranscriptToolCall[],
  events: TranscriptMarker[],
  endedAtMs: number | null,
): Entry[] {
  const out: Entry[] = []
  /**
   * DOES THIS PAYLOAD HAVE A CLOCK AT ALL?
   *
   * A turn's anchors are absent for two quite different reasons and this asks
   * about the second: the sideband was detached for THAT turn (one turn, a
   * marker beside it), or the payload was produced before per-turn timestamps
   * were sent at all (EVERY turn, no marker, nothing wrong with the call). The
   * second is the whole history of the end-of-session webhook up to
   * `EndWebhookTurn.at`, so it is not an edge case — it is most stored payloads.
   *
   * The two paths below are genuinely different orderings, not one with a
   * fallback, which is why this is decided once up front rather than per turn.
   */
  const clocked = turns.some(
    (t) =>
      t.userSpeechStoppedAt != null ||
      t.clientSpeechEndAt != null ||
      t.responseCreatedAt != null ||
      t.audioBufferStartedAt != null ||
      t.responseDoneAt != null,
  )

  if (!clocked) return unclocked(turns, toolCalls)

  let cursor = 0
  for (const t of turns) {
    const userAt = t.userSpeechStoppedAt ?? t.clientSpeechEndAt ?? t.responseCreatedAt
    if (t.userText) {
      cursor = Math.max(cursor + 1, userAt ?? cursor + 1)
      // A user line's anchor IS its end — speech-stopped — so `until` repeats it.
      out.push({ kind: 'said', at: cursor, until: cursor, who: 'user', n: t.n, text: t.userText })
    }
    const asstAt = t.audioBufferStartedAt ?? t.responseCreatedAt ?? t.responseDoneAt
    if (t.assistantText) {
      cursor = Math.max(cursor + 1, asstAt ?? cursor + 1)
      out.push({
        kind: 'said',
        at: cursor,
        until: t.audioBufferStoppedAt,
        who: 'assistant',
        n: t.n,
        text: t.assistantText,
      })
    }
  }
  for (const c of toolCalls) out.push({ kind: 'tool', at: c.startedAt, until: c.endedAt, call: c })
  for (const e of events) {
    if (isGapEvent(e.kind, e.atMs, endedAtMs)) out.push({ kind: 'marker', at: e.atMs, event: e })
  }
  return out.sort((a, b) => (a.at ?? 0) - (b.at ?? 0))
}

/**
 * READING ORDER FOR A PAYLOAD WITH NO TURN CLOCK — built, not sorted.
 *
 * SORTING IS WHAT BREAKS HERE, and it broke in a way worth writing down. The
 * clocked path gives an anchorless turn a synthetic `at` of 1, 2, 3… so that it
 * keeps its place; tool calls meanwhile carry a REAL `startedAt` of about
 * 1.79e12. Sort those together and every tool call lands after every line of the
 * conversation — on a real session that read as "the agent said all of this, and
 * then made two weather lookups at the end", which is not what happened.
 *
 * So order comes from the turn NUMBERS, which are always present and always
 * correct, and tool calls slot in by `turnN`.
 *
 * A TOOL CALL GOES AFTER ITS TURN'S ROWS, not between the question and the
 * answer. On a tool turn the assistant's text is the filler said BEFORE the
 * result arrives ("let me check the current conditions") and the real answer is
 * in the NEXT turn — so after is where it actually happened. `turnN: null` means
 * it never attached to a turn, and those go last rather than being guessed into
 * the middle.
 *
 * TURN ROWS GET `at: null`, tool rows keep their real `startedAt`. That
 * asymmetry is honest: we know when the tool ran because the row records it, and
 * we do not know when the words were said. The alternative is inventing a time
 * for the words or hiding the one time we have.
 */
function unclocked(turns: TranscriptTurn[], toolCalls: TranscriptToolCall[]): Entry[] {
  const byTurn = new Map<number, TranscriptToolCall[]>()
  const unattached: TranscriptToolCall[] = []
  for (const c of toolCalls) {
    if (c.turnN === null) {
      unattached.push(c)
      continue
    }
    const list = byTurn.get(c.turnN)
    if (list) list.push(c)
    else byTurn.set(c.turnN, [c])
  }

  const out: Entry[] = []
  for (const t of turns) {
    if (t.userText) {
      out.push({ kind: 'said', at: null, until: null, who: 'user', n: t.n, text: t.userText })
    }
    if (t.assistantText) {
      out.push({ kind: 'said', at: null, until: null, who: 'assistant', n: t.n, text: t.assistantText })
    }
    for (const c of byTurn.get(t.n) ?? []) {
      out.push({ kind: 'tool', at: c.startedAt, until: c.endedAt, call: c })
    }
  }
  for (const c of unattached) out.push({ kind: 'tool', at: c.startedAt, until: c.endedAt, call: c })
  return out
}

/** `args => result`, in the shape a person pastes into a bug report. */
export function toolDump(c: TranscriptToolCall): string {
  const args = c.args == null ? '' : JSON.stringify(c.args, null, 2)
  const result = c.result == null ? '(no output)' : JSON.stringify(c.result, null, 2)
  return `${c.name}(${args})\n=> ${result}`
}
